import { trace } from "@opentelemetry/api";
import pageSpanStore from "$lib/stores/pageSpanStore";
import { get } from "svelte/store";
import { page } from "$app/stores";

export const buildAttributes = (attributes = {}) => {
  return {
    ...attributes,
    uri: page.url
  };
};
/*
SOURCE: OpenTelemetry spec → “Tracing API” → Retrieving the TraceId and SpanId: https://opentelemetry.io/docs/specs/otel/trace/api/#retrieving-the-traceid-and-spanid
Canonical traceId must be a 32-char lowercase hex string
Canonical SpanId must be a 16-char lowercase hex string.

SOURCE: Datadog → APM → Trace Context Propagation: https://docs.datadoghq.com/tracing/trace_collection/trace_context_propagation/?tab=nodejs#custom-header-formats
In the HTTP header list:
• x-datadog-trace-id: “Specifies the lower 64-bits of the 128-bit trace-id, in decimal format.”
• x-datadog-parent-id: “Specifies the 64-bits span-id … in decimal format.”

SOURCE: Datadog → OpenTelemetry section → Reference → Trace IDs : https://docs.datadoghq.com/opentelemetry/reference/trace_ids/
Explains Datadog’s 128-bit trace IDs have randomness in the lower-order 64 bits for backward compatibility with systems that use 64-bit IDs. Practically, when bridging to places that only accept 64-bit IDs (like the headers above or log fields), you use the lower 64 bits—which you then encode in decimal per the header/log requirements
TL;DR: 
Put dd.trace_id (decimal, low 64 bits of OTel 128-bit traceId) and dd.span_id (decimal from OTel spanId) on each log line, 
and ensure service/env match our trace Resource. That’s the supported path to clickable log↔trace correlation in Datadog.
*/
function hex64ToDecString(hex) {
  const HEX = "0123456789abcdef";
  let dec = "0";
  for (let i = 0; i < hex.length; i++) {
    const n = HEX.indexOf(hex[i].toLowerCase());
    if (n < 0) throw new Error("Invalid hex");
    // dec = dec * 16 + n
    dec = mulDecBySmall(dec, 16);
    dec = addDecSmall(dec, n);
  }
  return stripLeadingZeros(dec);
}
function mulDecBySmall(decStr, m) {
  let carry = 0,
    out = "";
  for (let i = decStr.length - 1; i >= 0; i--) {
    const x = (decStr.charCodeAt(i) - 48) * m + carry;
    out = String.fromCharCode(48 + (x % 10)) + out;
    carry = Math.floor(x / 10);
  }
  return carry ? carry.toString() + out : out;
}
function addDecSmall(decStr, a) {
  let carry = a,
    out = "";
  for (let i = decStr.length - 1; i >= 0; i--) {
    const x = decStr.charCodeAt(i) - 48 + carry;
    out = String.fromCharCode(48 + (x % 10)) + out;
    carry = Math.floor(x / 10);
  }
  return carry ? carry.toString() + out : out;
}
function stripLeadingZeros(s) {
  let i = 0;
  while (i < s.length - 1 && s[i] === "0") i++;
  return s.slice(i);
}

export function otelTraceIdToDdDecimal(traceIdHex) {
  if (!traceIdHex || traceIdHex.length !== 32) return undefined;
  const low64 = traceIdHex.slice(16); // lower 64 bits
  return hex64ToDecString(low64);
}
export function otelSpanIdToDdDecimal(spanIdHex) {
  if (!spanIdHex || spanIdHex.length !== 16) return undefined;
  return hex64ToDecString(spanIdHex);
}
// Function to ensure all custom keys are prefixed with "of.*" for easy locating in DD
export function ofAttrs(obj = {}) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;

    // keep well-known keys untouched; prefix everything else
    const keep =
      k.startsWith("of.") ||
      k.startsWith("dd.") ||
      k.startsWith("otel.") ||
      k === "service" ||
      k === "service_name" ||
      k === "env" ||
      k === "source" ||
      k === "ddsource" ||
      k === "deployment.environment";

    out[keep ? k : `of.${k}`] = v;
  }
  return out;
}

export function ofMerge(...objs) {
  return objs.reduce((acc, o) => Object.assign(acc, ofAttrs(o || {})), {});
}

// Use context to get active span or derive non-active span from context
export function spanFromCtxOrFallback(ctx) {
  // 1) preferred: span from provided context
  const s1 = ctx ? trace.getSpan(ctx) : undefined;
  if (s1) return s1;

  // 2) active span
  const s2 = trace.getActiveSpan?.();
  if (s2) return s2;

  // 3) page span (wrap into a context so IDs exist)
  const pg = get(pageSpanStore)?.span;
  return pg || undefined;
}
